import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import planApi from '../../services/planApi';
import config from '../../config';
import {
  Users, Calendar, TrendingUp, Clock, ArrowRight, CheckCircle, ListChecks,
  Compass, CalendarDays, Lock, ShieldCheck
} from 'lucide-react';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { useToast } from '../../hooks/useToast';
import MainLayout from '../layout/MainLayout';
import Card, { CardHeader, CardTitle, CardBody } from '../core/Card/Card';
import NotificationsWidget from '../common/NotificationsWidget';
import '../../styles/mentee-dashboard.scss';

const getImageUrl = (imagePath?: string | null): string | null => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  const baseUrl = config.api.baseUrl.replace(/\/api\/?$/, '');
  return `${baseUrl}/${imagePath}`;
};

const fmtDateTime = (sql?: string) => {
  if (!sql) return '—';
  const d = new Date(String(sql).replace(' ', 'T'));
  return isNaN(d.getTime()) ? String(sql) : d.toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' });
};

type StageState = 'done' | 'active' | 'locked';

interface MenteeStat {
  progress: number; done: number; total: number; comps: number; sessions: any[];
  closedComps: number; monthsRemaining: number;
  consentOk: boolean; fodaDone: boolean; pdiDone: boolean;
  menteeSigned: boolean; mentorSigned: boolean;
  menteeAnswers: Record<string, string> | null; mentorAnswers: Record<string, string> | null;
}

const MentorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [dash, setDash] = useState<any>(null);
  const [stats, setStats] = useState<Record<number, MenteeStat>>({});

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const load = async () => {
    try {
      setLoading(true);
      const dResp = await apiService.mentor.dashboard();
      const d = dResp.data.data;
      setDash(d);
      const mentees: any[] = d?.active_mentees || [];
      const results = await Promise.all(mentees.map(async (m) => {
        const [pdi, s, pl] = await Promise.all([
          planApi.getPdi(m.id).catch(() => ({ competencies: [] })),
          planApi.getSessions(m.id).catch(() => ({ sessions: [] })),
          planApi.getPlan(m.id).catch(() => null),
        ]);
        const comps: any[] = (pdi as any)?.competencies || [];
        const total = comps.reduce((n, c) => n + (Number(c.actionsTotal) || 0), 0);
        const done = comps.reduce((n, c) => n + (Number(c.actionsDone) || 0), 0);
        const closedComps = comps.filter((c) => Number(c.actionsTotal) > 0 && Number(c.actionsDone) === Number(c.actionsTotal)).length;
        const consent = (pl as any)?.consent || {};
        const stat: MenteeStat = {
          progress: total > 0 ? Math.round((done / total) * 100) : 0,
          done, total, comps: comps.length, sessions: (s as any)?.sessions || [],
          closedComps, monthsRemaining: (pl as any)?.program?.months_remaining ?? 11,
          consentOk: (pl as any)?.locks?.fodaEnabled === true,
          fodaDone: (pl as any)?.stages?.foda?.status === 'finalized',
          pdiDone: (pl as any)?.stages?.pdi?.status === 'finalized',
          menteeSigned: consent.mentee_signed === true, mentorSigned: consent.mentor_signed === true,
          menteeAnswers: consent.mentee_answers || null, mentorAnswers: consent.mentor_answers || null,
        };
        return [m.id, stat] as [number, MenteeStat];
      }));
      setStats(Object.fromEntries(results));
    } catch (err: any) {
      showError(err?.response?.data?.message || 'Error al cargar el dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="mentee-dashboard mentee-dashboard--loading">
          <LoadingSpinner size="lg" text="Cargando panel..." />
        </div>
      </MainLayout>
    );
  }

  const mentees: any[] = dash?.active_mentees || [];
  const allStats = Object.values(stats);
  const avgProgress = allStats.length
    ? Math.round(allStats.reduce((n, s) => n + s.progress, 0) / allStats.length) : 0;
  // Métricas equivalentes a las del mentee (agregadas sobre los mentees del mentor).
  const totalObjectives = allStats.reduce((n, s) => n + s.comps, 0);
  const closedObjectives = allStats.reduce((n, s) => n + s.closedComps, 0);
  const sessionsTarget = Math.max(1, mentees.length) * 12;
  const monthsRemaining = allStats.length ? allStats[0].monthsRemaining : 11;

  // Sesiones pendientes de aprobación (a través de todos los mentees)
  const pending = mentees.flatMap((m) =>
    (stats[m.id]?.sessions || [])
      .filter((s: any) => s.status === 'pending')
      .map((s: any) => ({ ...s, mentee: m }))
  ).sort((a, b) => String(a.scheduled_date).localeCompare(String(b.scheduled_date)));

  const totalCompleted = mentees.reduce((n, m) =>
    n + (stats[m.id]?.sessions || []).filter((s: any) => s.status === 'completed').length, 0);

  // ---- Estado del plan (agregado sobre los mentees del mentor) ----
  const nMentees = allStats.length;
  const nConsent = allStats.filter((s) => s.consentOk).length;
  const nFoda = allStats.filter((s) => s.fodaDone).length;
  const nPdi = allStats.filter((s) => s.pdiDone).length;
  const aggState = (dones: number, enabled: number): StageState =>
    (nMentees > 0 && dones === nMentees) ? 'done' : (enabled > 0 ? 'active' : 'locked');
  // El mentor no tiene FODA/PDI propios: revisa los del mentee en "Mi Mentee".
  const menteeLink = '/mentor/mentees';
  const stages: { key: string; label: string; state: StageState; icon: React.ReactNode; hint: string; to: string }[] = [
    {
      key: 'foda', label: 'FODA', state: aggState(nFoda, nConsent),
      icon: <Compass size={18} />,
      hint: nMentees === 0 ? 'Sin mentees' : (nFoda === nMentees ? 'Tu mentee lo completó · ver' : 'Tu mentee aún no lo completa'),
      to: menteeLink,
    },
    {
      key: 'pdi', label: 'Plan de Desarrollo Individual', state: aggState(nPdi, nFoda),
      icon: <ListChecks size={18} />,
      hint: nMentees === 0 ? 'Sin mentees' : `${avgProgress}% de avance`,
      to: menteeLink,
    },
    {
      key: 'agenda', label: 'Sesiones', state: nConsent > 0 ? 'active' : 'locked',
      icon: <CalendarDays size={18} />,
      hint: `${totalCompleted} sesión(es) hechas`,
      to: '/mentor/sessions',
    },
  ];

  const objectivesOf = (s: any): string[] =>
    (s.objectives && s.objectives.length) ? s.objectives : (s.objective ? [s.objective] : []);

  const accept = async (sessionId: number) => {
    try { await planApi.acceptSession(sessionId); await load(); }
    catch (e: any) { showError(e?.response?.data?.message || 'No se pudo aceptar'); }
  };
  const reject = async (sessionId: number) => {
    try { await planApi.rejectSession(sessionId); await load(); }
    catch (e: any) { showError(e?.response?.data?.message || 'No se pudo rechazar'); }
  };

  return (
    <MainLayout>
      <div className="mentee-dashboard">
        <div className="page-header">
          <div className="page-header__info">
            <div className="eyebrow">Mentor</div>
            <h1 className="heading-1">Panel de <em>Mentoría</em></h1>
          </div>
          <div className="page-header__actions">
            <button className="btn btn--primary" onClick={() => navigate('/mentor/sessions')}>
              Nueva sesión <ArrowRight size={16} />
            </button>
          </div>
        </div>

        {/* Aviso: acuerdo del mentor pendiente (solo si aún no lo firma) */}
        {allStats.some((s) => !s.mentorSigned) && (
          <div className="consent-alert">
            <div className="consent-alert__box" role="alert">
              <span className="consent-alert__icon"><ShieldCheck size={20} /></span>
              <div className="consent-alert__text">
                <strong>Tu Acuerdo de Mentoría está pendiente.</strong> Fírmalo para dejar claras tus expectativas con tu mentee.
              </div>
              <button className="consent-alert__cta" onClick={() => navigate('/mentor/plan')}>
                Revisar y firmar <ArrowRight size={15} />
              </button>
            </div>
          </div>
        )}

        {/* Estado del plan */}
        <div className="plan-status" role="navigation" aria-label="Estado del plan">
          {stages.map((s, i) => (
            <button
              key={s.key}
              className={`plan-status__step plan-status__step--${s.state}`}
              onClick={() => navigate(s.to)}
              title={`Ir a ${s.label}`}
            >
              <span className="plan-status__icon">
                {s.state === 'done' ? <CheckCircle size={18} /> : s.state === 'locked' ? <Lock size={16} /> : s.icon}
              </span>
              <span className="plan-status__text">
                <span className="plan-status__label">{s.label}</span>
                <span className="plan-status__hint">{s.hint}</span>
              </span>
              {i < stages.length - 1 && <span className="plan-status__sep" aria-hidden />}
            </button>
          ))}
        </div>

        <div className="mkpis">
          <div className="mkpi">
            <div className="mkpi__num mkpi__num--red">{avgProgress}<span className="mkpi__unit">%</span></div>
            <div className="mkpi__lbl">Avance del PDI</div>
          </div>
          <div className="mkpi">
            <div className="mkpi__num">{totalCompleted}<span className="mkpi__den">/{sessionsTarget}</span></div>
            <div className="mkpi__lbl">Sesiones</div>
          </div>
          <div className="mkpi">
            <div className="mkpi__num">{closedObjectives}<span className="mkpi__den">/{totalObjectives}</span></div>
            <div className="mkpi__lbl">Objetivos cerrados</div>
          </div>
          <div className="mkpi">
            <div className="mkpi__num">{monthsRemaining}</div>
            <div className="mkpi__lbl">Meses restantes</div>
          </div>
        </div>

        <div className="dashboard-grid">
          {/* Mentees con su avance del PDI */}
          <div className="dashboard-grid__main">
            <Card animate animateDelay={0.2}>
              <CardHeader><CardTitle>Mis Mentees · avance del PDI</CardTitle></CardHeader>
              <CardBody>
                {mentees.length === 0 ? (
                  <p className="dash-empty">Aún no tienes mentees asignados.</p>
                ) : (
                  <div className="mentee-progress-list">
                    {mentees.map((m) => {
                      const st = stats[m.id] || { progress: 0, done: 0, total: 0, comps: 0, sessions: [] };
                      const next = (st.sessions || [])
                        .filter((s: any) => ['scheduled', 'pending'].includes(s.status))
                        .sort((a: any, b: any) => String(a.scheduled_date).localeCompare(String(b.scheduled_date)))[0];
                      return (
                        <div className="mentee-progress" key={m.id}>
                          <div className="mentee-progress__avatar">
                            {getImageUrl(m.mentee_image) ? (
                              <img src={getImageUrl(m.mentee_image) as string} alt={m.mentee_first_name}
                                style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                            ) : (
                              <span>{m.mentee_first_name?.[0]}{m.mentee_last_name?.[0]}</span>
                            )}
                          </div>
                          <div className="mentee-progress__body">
                            <div className="mentee-progress__head">
                              <span className="mentee-progress__name">{m.mentee_first_name} {m.mentee_last_name}</span>
                              <span className="mentee-progress__pct">{st.progress}%</span>
                            </div>
                            <div className="comp-progress__bar">
                              <div className="comp-progress__fill" style={{ width: `${st.progress}%` }} />
                            </div>
                            <div className="mentee-progress__meta">
                              <span><ListChecks size={13} /> {st.done}/{st.total} acciones · {st.comps} competencias</span>
                              {next && <span><Clock size={13} /> {fmtDateTime(next.scheduled_date)}</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardBody>
            </Card>
          </div>

          {/* Notificaciones */}
          <div className="dashboard-grid__sidebar">
            <NotificationsWidget limit={5} animateDelay={0.25} />
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default MentorDashboard;
